import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, FileText, Eye, EyeOff, Send,
  CheckCircle2, AlertTriangle, MinusCircle, Sparkles,
  ChevronRight, RefreshCw, Copy, Building2,
  Calendar, Clock, History, Activity, Shield, AlertCircle,
  Loader2, X, ScanLine, Lock, Unlock, ShieldAlert, XCircle,
  ListChecks, Settings, Key, FileBarChart2,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { applyImpersonation } from "../../lib/impersonation";
import { useAuth } from "../../context/AuthContext";
import { PIIRevealConfirm } from "../../components/admin/PIIRevealConfirm";
import CancelMerchantsModal from "./components/CancelMerchantsModal";
import SLAPill from "./components/SLAPill";
import MerchantNotes from "./merchant-detail/MerchantNotes";
import MerchantRegions from "./merchant-detail/MerchantRegions";
// ShopCredentialsModal import removed Feb 27 2026 — its functionality
// is merged into APICredentialsPanel as the "Gateway (shop) keys"
// sub-block.
import DeclineMerchantModal from "./merchant-detail/DeclineMerchantModal";
import ScreeningHeadline from "../../components/admin/ScreeningHeadline";
import MerchantSolutions from "../../components/admin/MerchantSolutions";
import TransactionMilestonesPanel from "../../components/admin/TransactionMilestonesPanel";
import KnowledgePreview from "../../components/admin/KnowledgePreview";
// Brief 3.6 Phase C7 (Feb 20, 2026) — TelegramOpsPanel removed with the rest
// of the Telegram code path.
// Brief 3.5 PR 2 — IA refactor: tabbed merchant page (Review / Account /
// History) with a recommendation banner + status stepper on Review.
import AdminMerchantReports from "./AdminMerchantReports";
import MerchantTabs from "../../components/admin/merchant/MerchantTabs";
import RecommendationBanner from "../../components/admin/merchant/RecommendationBanner";
import ReReviewRibbon from "../../components/admin/merchant/ReReviewRibbon";
import StatusStepper from "../../components/admin/merchant/StatusStepper";
import useMerchantVerdict from "../../lib/useMerchantVerdict";
import { plainStatus } from "../../lib/plainLanguage";
// Brief 3.5 PR 6.1 — file-split refactor. Sections lifted from this
// page into /pages/admin/merchant-detail/ so the lint gate in PR 7 can
// run on small, focused files.
import {
  Section, CollapsibleSection, SegmentedFilter, fmtDate, fmtRelative,
} from "./merchant-detail/primitives";
import DocumentsTable from "./merchant-detail/DocumentsTable";
import ComplianceReportActions from "./merchant-detail/ComplianceReportActions";
// Brief 3.5 PR 7-A — further file split. Parent shrunk 1975 → ~650
// lines so PR 7-B's lint gate runs on a small, focused surface.
import { FormDataPanel } from "./merchant-detail/FormDataPanel";
import { DecisionPanel, DecisionBanner } from "./merchant-detail/DecisionPanel";
import { ActivityTimeline } from "./merchant-detail/ActivityTimeline";
import {
  APICredentialsPanel,
} from "./merchant-detail/APICredentialsPanel";
import { DiagnosticPanel } from "./merchant-detail/DiagnosticPanel";

/**
 * Brief 3.6.1 — Unified merchant page (read-only).
 *
 * Single-screen command center for an admin reviewing a merchant. Sections
 * top-to-bottom (per spec §1.2):
 *
 *   A — Identity (top of page; scrolls with content — no longer sticky
 *       since the block has grown enough that pinning blocks the viewport)
 *   B — Cross-check summary (counts; never recommends)
 *   C — Documents (KYB + financials, unified table)
 *   D — Form data (collapsed by default)
 *   E — Decision interface (visible but DISABLED in 3.6.1; lands in 3.6.2)
 *   F — Activity timeline (default-expanded for active review states:
 *       awaiting_documents / submitted / under_review; collapsed for
 *       prospect / draft / approved / live / declined / terminated.
 *       Source of truth: backend services/merchant_states.py)
 *
 * Three round trips on mount, all in parallel:
 *   GET /admin/merchants/{id}/unified         — sections A, C, D, F
 *   GET /admin/merchants/{id}/cross-check     — section B
 *   GET /admin/onboarding-links?status=all    — Identity-block magic-link card
 */
export default function AdminMerchantDetail() {
  const { merchantId } = useParams();
  const [unified, setUnified] = useState(null);
  const [crossCheck, setCrossCheck] = useState(null);
  const [magicLink, setMagicLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  // Brief 3.5 PR 3 — single source of truth verdict. Re-fetches on
  // every refresh() so it stays in lock-step with the rest of the page.
  const { verdict } = useMerchantVerdict(merchantId, reloadKey);

  const refresh = async () => {
    setLoading(true);
    try {
      const [u, c, m] = await Promise.all([
        api.get(`/admin/merchants/${merchantId}/unified`),
        api.get(`/admin/merchants/${merchantId}/cross-check`).catch(() => ({ data: null })),
        api.get(`/admin/onboarding-links`, { params: { status: "all" } }).catch(() => ({ data: null })),
      ]);
      setUnified(u.data);
      setCrossCheck(c.data);
      const myLinks = (m.data?.links || []).filter((l) => l.merchant_id === merchantId);
      setMagicLink(myLinks[0] || null);
      setError("");
      // Brief 3.5 PR 3 — bump verdict reloadKey so the verdict hook
      // refetches in step with the rest of the page on refresh().
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not load merchant.");
    } finally {
      setLoading(false);
    }
  };

  // Deliberate set-state-in-effect: this IS the load-on-mount /
  // reload-on-id-change pattern. refresh() updates loading/unified/etc.
  // React Compiler flags it because it can't prove the effect is the
  // only writer; verified manually that no other code path mutates
  // these on render.
  /* eslint-disable */
  useEffect(() => { refresh(); }, [merchantId]);
  /* eslint-enable */

  if (loading) {
    return (
      <div className="text-zinc-500 text-[12px] uppercase tracking-[0.22em]" data-testid="merchant-loading">
        Loading merchant…
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="merchant-error" className="rounded-2xl border border-rose-400/20 bg-rose-400/[0.03] p-5 text-rose-200 text-[13px]">
        {error}
      </div>
    );
  }
  if (!unified) return null;

  return (
    <div data-testid="merchant-detail-page" className="max-w-7xl space-y-7 pb-16">
      <Link
        to="/admin/merchants"
        data-testid="merchant-back-to-list"
        className="inline-flex items-center gap-1.5 text-[12px] text-zinc-500 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-3 w-3" /> All merchants
      </Link>

      {/* Section A — Identity (top of page, visible on every tab) */}
      <IdentityBlock
        identity={unified.identity}
        magicLink={magicLink}
        merchantId={merchantId}
        onMagicLinkRegenerated={refresh}
        onCancelled={refresh}
        documents={unified.documents}
        crossCheck={crossCheck}
      />

      {/* Feb-21 2026 — Notes + Regions sit right under Identity, above
          the tab bar, because both are AM working-context (not
          merchant data). Notes replaces the old Quick Share textarea;
          Regions captures sales-targeting context for the pipeline. */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="merchant-am-context">
        <MerchantNotes merchantId={merchantId} />
        <MerchantRegions merchantId={merchantId} />
      </div>

      {/* Brief 3.5 PR 2 — IA tabs. Identity stays above the tab bar so it
          is visible on every tab. Each tab lazy-renders its own subtree
          so the heavy Account tab (5+ panels) doesn't render unless the
          operator switches to it. */}
      <MerchantTabs
        merchantId={merchantId}
        tabs={[
          {
            key: "review",
            label: "Review",
            icon: ListChecks,
            render: () => (
              <>
                <ReReviewRibbon
                  merchantId={merchantId}
                  identity={unified.identity}
                  verdict={verdict}
                />
                <StatusStepper identity={unified.identity} />
                <RecommendationBanner
                  identity={unified.identity}
                  verdict={verdict}
                  documents={unified.documents}
                />
                <CrossCheckSummary
                  merchantId={merchantId}
                  crossCheck={crossCheck}
                  verdict={verdict}
                />
                <div data-testid="merchant-detail-screening-headline-wrap">
                  <ScreeningHeadline merchantId={merchantId} />
                </div>
                <DocumentsTable docs={unified.documents} merchantId={merchantId} onChanged={refresh} />
                <ComplianceReportActions
                  merchantId={merchantId}
                  legalName={unified.identity?.legal_name || "merchant"}
                  lastRender={unified.compliance_report_last_render}
                />
                <DecisionPanel
                  identity={unified.identity}
                  merchantId={merchantId}
                  onTransitioned={refresh}
                />
              </>
            ),
          },
          {
            key: "account",
            label: "Account",
            icon: Settings,
            render: () => (
              <>
                <MerchantSolutions merchantId={merchantId} />
                <FormDataPanel
                  data={unified.form_data}
                  merchantId={merchantId}
                  identity={unified.identity}
                />
                {(unified.identity.status === "approved" || unified.identity.status === "live") && (
                  <APICredentialsPanel merchantId={merchantId} status={unified.identity.status} />
                )}
                <TransactionMilestonesPanel merchantId={merchantId} status={unified.identity.status} />
              </>
            ),
          },
          {
            key: "history",
            label: "History",
            icon: History,
            render: () => (
              <>
                <ActivityTimeline activity={unified.activity} />
                {/* Super_admin-only diagnostic panels: KnowledgePreview +
                    DiagnosticPanel self-hide internally for non-super_admin,
                    so non-super_admins simply see an empty space on this
                    tab below the timeline (per Dioni signoff: option a). */}
                <KnowledgePreview merchantId={merchantId} />
                <DiagnosticPanel merchantId={merchantId} />
              </>
            ),
          },
          {
            // Brief 3.13 (Feb 25 2026) — Reports tab. Houses three sub-tabs
            // (Financial reports / Performance / Proof of payment). The
            // AdminMerchantReports panel was previously orphaned — not
            // mounted anywhere — so AMs had no way to upload weekly
            // performance reports or proof-of-payment files / crypto-txn
            // URLs even though the backend already supported it.
            key: "reports",
            label: "Reports",
            icon: FileBarChart2,
            render: () => (
              <AdminMerchantReports merchantId={merchantId} />
            ),
          },
        ]}
      />
    </div>
  );
}

// ---------- A. Identity ----------

const STATUS_THEME = {
  prospect:           { label: "Prospect",           cls: "border-white/[0.10] bg-white/[0.04] text-zinc-300" },
  draft:              { label: "Draft",              cls: "border-white/[0.10] bg-white/[0.04] text-zinc-300" },
  awaiting_documents: { label: "Awaiting docs",      cls: "border-amber-400/30 bg-amber-400/[0.08] text-amber-100" },
  submitted:          { label: "Submitted",          cls: "border-cyan-400/30 bg-cyan-400/[0.08] text-cyan-100" },
  under_review:       { label: "Under review",       cls: "border-violet-400/30 bg-violet-400/[0.08] text-violet-100" },
  info_requested:     { label: "Info requested",     cls: "border-amber-400/30 bg-amber-400/[0.10] text-amber-100" },
  approved:           { label: "Approved",           cls: "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100" },
  live:               { label: "Live",               cls: "border-emerald-400/40 bg-emerald-400/[0.10] text-emerald-50" },
  declined:           { label: "Declined",           cls: "border-rose-400/30 bg-rose-400/[0.08] text-rose-100" },
  cancelled:          { label: "Cancelled",          cls: "border-zinc-500/30 bg-zinc-500/[0.08] text-zinc-300" },
  suspended:          { label: "Suspended",          cls: "border-rose-400/30 bg-rose-400/[0.08] text-rose-100" },
  terminated:         { label: "Terminated",         cls: "border-rose-400/40 bg-rose-400/[0.10] text-rose-50" },
};

function StatusPill({ status }) {
  const cfg = STATUS_THEME[status] || STATUS_THEME.draft;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border ${cfg.cls} px-3 py-1 text-[12px] tracking-wide`}
      data-testid={`merchant-status-pill-${status}`}
    >
      {cfg.label}
    </span>
  );
}

function IdentityBlock({ identity, magicLink, merchantId, onMagicLinkRegenerated, onCancelled, documents, crossCheck }) {
  const [phone, setPhone] = useState(null);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  // Brief 3.6.5 (B) — same set as services/merchant_states.CANCELLABLE_STATES.
  // Keep in lockstep; backend is the source of truth, this is just for
  // disabling the button + tooltip on terminal states.
  const isCancellable = [
    "prospect", "draft", "awaiting_documents", "submitted",
    "under_review", "info_requested", "approved", "declined",
  ].includes(identity.status);

  const onRevealPhone = () => {
    if (phone) { setPhone(null); return; }
    setPhoneModalOpen(true);
  };
  const onPhoneConfirmed = (data) => {
    setPhone(data?.phone || "—");
    setPhoneModalOpen(false);
  };

  const onRegenLink = async () => {
    if (!window.confirm("Regenerate the onboarding magic link? The current link will be invalidated immediately.")) return;
    try {
      const { data } = await api.post(`/admin/merchants/${merchantId}/regenerate-onboarding-link`);
      await navigator.clipboard.writeText(data.magic_link_url).catch(() => {});
      onMagicLinkRegenerated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert((detail && typeof detail === "object" ? detail.message : detail) || "Couldn't regenerate.");
    }
  };

  return (
    <section
      data-testid="merchant-identity"
      className="relative"
    >
      <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6">
        {identity.info_request && (
          <DecisionBanner kind="info_request" payload={identity.info_request} />
        )}
        {identity.decline_reason && (
          <DecisionBanner kind="decline" payload={identity.decline_reason} />
        )}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <Building2 className="h-4 w-4 text-zinc-500" />
              <h1 className="font-display text-[28px] sm:text-[32px] tracking-tighter text-white truncate" data-testid="merchant-legal-name">
                {identity.legal_name || identity.merchant_email || "Untitled merchant"}
              </h1>
              {identity.ref && (
                <span className="ref-mono text-[12px] text-zinc-500">· {identity.ref}</span>
              )}
              <StatusPill status={identity.status} />
              {identity.sla && identity.sla.state !== "not_applicable" && (
                <SLAPill sla={identity.sla} testId="merchant-sla-pill" />
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12.5px] text-zinc-400">
              {identity.vertical && <span>{identity.vertical}</span>}
              {identity.country_of_incorporation && <span>· {identity.country_of_incorporation}</span>}
              {identity.company_registration_number && <span className="ref-mono">· #{identity.company_registration_number}</span>}
              {identity.primary_website && (
                <a
                  href={identity.primary_website.startsWith("http") ? identity.primary_website : `https://${identity.primary_website}`}
                  target="_blank" rel="noreferrer"
                  data-testid="merchant-website-link"
                  className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 transition-colors"
                >
                  · {identity.primary_website} <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Dates row */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 text-[12px]">
          <DateChip icon={Calendar} label="Created" value={identity.created_at} />
          <DateChip icon={Calendar} label="Submitted" value={identity.submitted_at} />
          <DateChip icon={Clock} label="Last status change" value={identity.last_status_change_at} />
          <DateChip icon={Clock} label="Last activity" value={identity.updated_at} />
        </div>

        {/* Contact + magic link in two columns */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-xl border border-white/[0.06] bg-white/[0.015] p-4" data-testid="merchant-primary-contact">
            <div className="flex items-center justify-between">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">— Primary contact</div>
              {identity.primary_contact?.missing_fields?.length > 0 && (
                <span
                  className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-amber-500/[0.10] text-amber-300 border border-amber-400/30"
                  data-testid="merchant-primary-contact-missing-badge"
                  title={`Missing: ${identity.primary_contact.missing_fields.map((k) => k.replace("primary_contact_", "")).join(", ")}. Pre-Feb-2026 submission — admin edit form (Phase 5) will let you fill these in.`}
                >
                  Missing {identity.primary_contact.missing_fields.length} field{identity.primary_contact.missing_fields.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-[13px]">
              <div>
                <div className="text-zinc-400 text-[11.5px]">Name & role</div>
                <div className="mt-0.5 text-white">{identity.primary_contact?.name || "—"}{identity.primary_contact?.role ? ` · ${identity.primary_contact.role}` : ""}</div>
              </div>
              <div>
                <div className="text-zinc-400 text-[11.5px]">Email</div>
                <div className="mt-0.5 text-white">{identity.primary_contact?.email || "—"}</div>
              </div>
              <div>
                <div className="text-zinc-400 text-[11.5px]">Phone</div>
                <div className="mt-0.5 inline-flex items-center gap-2">
                  <span className="text-white" data-testid="merchant-contact-phone">
                    {phone === null && identity.primary_contact?.has_phone ? "•••• •••• ••••" : (phone || "—")}
                  </span>
                  {identity.primary_contact?.has_phone && (
                    <button
                      type="button"
                      onClick={onRevealPhone}
                      data-testid="merchant-contact-phone-reveal-btn"
                      title={phone ? "Hide" : "Reveal (audit-logged, requires reason)"}
                      className="text-zinc-500 hover:text-white transition-colors"
                    >
                      {phone ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-[11.5px]">Login email</div>
                <div className="mt-0.5 text-white">{identity.merchant_email || <span className="text-zinc-500">— (not signed up)</span>}</div>
              </div>
            </div>
          </div>

          {/* Magic link card */}
          <div
            className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4 flex flex-col"
            data-testid="merchant-magic-link-card"
          >
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">— Onboarding link</div>
            {!magicLink && (
              <div className="mt-2 flex-1 text-[12.5px] text-zinc-500">
                {identity.is_signed_up ? "Merchant already signed up." : "No active link. Mint one from /admin/onboarding-links."}
              </div>
            )}
            {magicLink && (
              <>
                <StatusPillCompact status={magicLink.status} isSignedUp={magicLink.is_signed_up} />
                <div className="mt-2.5 text-[11.5px] text-zinc-500">
                  Issued {fmtDate(magicLink.created_at)}
                  {magicLink.status === "active" && ` · expires ${fmtDate(magicLink.expires_at)}`}
                </div>
                {magicLink.status === "active" && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(magicLink.magic_link_url)}
                      data-testid="merchant-magic-link-copy"
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.08] text-white px-3 py-1.5 text-[11.5px] transition-colors"
                    >
                      <Copy className="h-3 w-3" /> Copy link
                    </button>
                    {!magicLink.is_signed_up && (
                      <button
                        type="button"
                        onClick={onRegenLink}
                        data-testid="merchant-magic-link-regen"
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.08] text-white px-3 py-1.5 text-[11.5px] transition-colors"
                      >
                        <RefreshCw className="h-3 w-3" /> Regenerate
                      </button>
                    )}
                  </div>
                )}
                {magicLink.status !== "active" && !magicLink.is_signed_up && (
                  <button
                    type="button"
                    onClick={onRegenLink}
                    data-testid="merchant-magic-link-regen"
                    className="mt-3 self-start inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.08] text-white px-3 py-1.5 text-[11.5px] transition-colors"
                  >
                    <RefreshCw className="h-3 w-3" /> Issue fresh link
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="mt-5 flex flex-wrap gap-2 pt-5 border-t border-white/[0.05]">
          <Link
            to={`/admin/merchants/${merchantId}/cross-check`}
            data-testid="merchant-quick-action-cross-check"
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-4 py-2 text-[12.5px] font-medium"
          >
            <Shield className="h-3.5 w-3.5" />
            Cross-check report
          </Link>
          {identity.is_signed_up && (
            <ViewAsMerchantButton
              merchantId={merchantId}
              legalName={identity.legal_name}
            />
          )}
          {/* "Shop credentials" pill removed Feb 26 2026. The unified
             "API credentials" inline section (APICredentialsPanel) is
             now the single entry point — see testid:
             shop-creds-issue-cta inside that section. */}
          <button
            type="button"
            onClick={() => setDeclineOpen(true)}
            disabled={["declined", "cancelled", "live"].includes(identity.status)}
            data-testid="merchant-quick-action-decline"
            title="Decline this application (compliance/risk rejection — distinct from cancel)"
            className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/30 bg-rose-400/[0.05] hover:bg-rose-400/[0.12] text-rose-100 px-4 py-2 text-[12.5px] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-rose-400/[0.05]"
          >
            <ShieldAlert className="h-3.5 w-3.5" />
            Decline
          </button>
          <button
            type="button"
            onClick={() => setCancelModalOpen(true)}
            disabled={!isCancellable}
            data-testid="merchant-quick-action-cancel"
            title={isCancellable
              ? "Soft-cancel this application (preserved for 7-year AML retention)"
              : `Cannot cancel from '${identity.status}'`}
            className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/30 bg-rose-400/[0.05] hover:bg-rose-400/[0.12] text-rose-100 px-4 py-2 text-[12.5px] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-rose-400/[0.05]"
          >
            <XCircle className="h-3.5 w-3.5" />
            Cancel application
          </button>
        </div>
      </div>
      <PIIRevealConfirm
        open={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        onConfirmed={onPhoneConfirmed}
        apiPath={`/admin/merchants/${merchantId}/reveal-contact-phone`}
        entityLabel="Primary contact phone"
        entityDescription="The merchant's primary contact phone number will be revealed once. Audit-logged with your reason."
        testId="phone-reveal-confirm"
      />
      {cancelModalOpen && (
        <CancelMerchantsModal
          merchants={[{
            merchant_id: merchantId,
            company_name: identity.legal_name || identity.merchant_email || merchantId,
            ref: identity.ref,
            status: identity.status,
          }]}
          onClose={() => setCancelModalOpen(false)}
          onSuccess={() => {
            setCancelModalOpen(false);
            onCancelled?.();
          }}
          testId="single-cancel-modal"
        />
      )}
      {declineOpen && (
        <DeclineMerchantModal
          merchantId={merchantId}
          legalName={identity.legal_name}
          onClose={() => setDeclineOpen(false)}
          onDeclined={() => {
            setDeclineOpen(false);
            onCancelled?.();
          }}
        />
      )}
    </section>
  );
}

function StatusPillCompact({ status, isSignedUp }) {
  const lookup = {
    active: { label: "Active", cls: "border-cyan-400/30 bg-cyan-400/[0.08] text-cyan-100" },
    used:   { label: isSignedUp ? "Onboarded" : "Used", cls: "border-emerald-400/30 bg-emerald-400/[0.08] text-emerald-100" },
    expired: { label: "Expired", cls: "border-amber-400/30 bg-amber-400/[0.08] text-amber-100" },
    superseded: { label: "Superseded", cls: "border-white/[0.10] bg-white/[0.04] text-zinc-300" },
  };
  const cfg = lookup[status] || lookup.expired;
  return (
    <span className={`mt-2 inline-flex self-start rounded-full border ${cfg.cls} px-2.5 py-1 text-[11px]`}>{cfg.label}</span>
  );
}

function DateChip({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="inline-flex items-center gap-1.5 text-zinc-500 text-[10.5px] uppercase tracking-[0.18em]">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="mt-1 text-zinc-200">{value ? fmtDate(value) : <span className="text-zinc-600">—</span>}</div>
    </div>
  );
}

// ---------- B. Cross-check summary ----------

function CrossCheckSummary({ merchantId, crossCheck, verdict }) {
  if (!crossCheck) {
    return (
      <Section icon={Shield} title="Cross-check summary" data-testid="merchant-section-cross-check-summary">
        <div className="text-[12.5px] text-zinc-500">Cross-check engine hasn&apos;t run for this merchant yet.</div>
      </Section>
    );
  }
  // Brief 3.5 PR 3 — prefer the server verdict's counts (single source
  // of truth). Fall back to the cross-check payload when the verdict
  // hasn't loaded yet so the section still renders with the previous
  // numbers during a refresh.
  const c = verdict?.severity_counts || crossCheck.counts || {};
  const s = verdict?.screening_counts || crossCheck.screening?.counts || {};
  const docsTotal = (crossCheck.extractions || []).length;
  const findingsResolved = (crossCheck.resolved_findings || []).length;
  return (
    <Section
      icon={Shield}
      title="Cross-check summary"
      testId="merchant-section-cross-check-summary"
      action={(
        <Link
          to={`/admin/merchants/${merchantId}/cross-check`}
          data-testid="merchant-cross-check-deep-link"
          className="inline-flex items-center gap-1.5 text-[12px] text-cyan-300 hover:text-cyan-200 transition-colors"
        >
          Full report <ExternalLink className="h-3 w-3" />
        </Link>
      )}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryGroup label="Documents" value={`${docsTotal} processed`}>
          <Stat label="Extractions" value={docsTotal} kind="muted" />
        </SummaryGroup>

        <SummaryGroup
          label="Findings (open)"
          value={`${(c.critical || 0) + (c.suspicious || 0) + (c.minor || 0)} open · ${findingsResolved} resolved`}
        >
          <Stat label="Critical" value={c.critical || 0} kind={c.critical ? "danger" : "muted"} />
          <Stat label="Suspicious" value={c.suspicious || 0} kind={c.suspicious ? "warn" : "muted"} />
          <Stat label="Minor" value={c.minor || 0} kind="muted" />
        </SummaryGroup>

        <SummaryGroup label="Screening" value={crossCheck.screening?.last_provider || "—"}>
          <Stat label="Confirmed" value={s.confirmed_match || 0} kind={s.confirmed_match ? "danger" : "muted"} />
          <Stat label="Possible" value={s.pending || 0} kind={s.pending ? "warn" : "muted"} />
          <Stat label="Dismissed" value={s.dismissed_match || 0} kind="muted" />
        </SummaryGroup>
      </div>
      <div className="mt-4 text-[11.5px] text-zinc-500 grid grid-cols-1 sm:grid-cols-2 gap-y-1">
        <div>Last extraction: <span className="text-zinc-400">{fmtRelative(crossCheck.last_extraction_at) || "—"}</span></div>
        <div>Last screening: <span className="text-zinc-400">{fmtRelative(crossCheck.screening?.last_run_at) || "—"}</span></div>
      </div>
    </Section>
  );
}

function SummaryGroup({ label, value, children }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">— {label}</div>
      <div className="mt-1.5 text-[14.5px] text-white">{value}</div>
      <div className="mt-3 flex gap-4">{children}</div>
    </div>
  );
}

function Stat({ label, value, kind }) {
  const tones = {
    danger: "text-rose-300",
    warn:   "text-amber-300",
    muted:  "text-zinc-400",
  };
  return (
    <div className="flex-1 min-w-0">
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className={`mt-1 font-display text-2xl tracking-tighter ${tones[kind] || tones.muted}`}>{value}</div>
    </div>
  );
}












/**
 * "View as merchant" — opens a short-lived impersonation session and
 * navigates to the merchant dashboard. The ImpersonationBanner mounted
 * in `AppShell` shows the AM they're in preview mode the whole time.
 */
function ViewAsMerchantButton({ merchantId, legalName }) {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    if (!window.confirm(
      `Sign in as ${legalName || merchantId} and view their back office? ` +
      "All actions you take while previewing are audit-logged. " +
      "Auto-exits in 60 minutes."
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/merchants/${merchantId}/impersonate`,
      );
      applyImpersonation({
        token: data.token,
        merchant_id: data.merchant_id,
        merchant_legal_name: data.merchant_legal_name,
        merchant_email: data.merchant_email,
        merchant_ref: data.merchant_ref,
        expires_at: data.expires_at,
        return_to: `/admin/merchants/${merchantId}`,
      });
      // Hard navigation — clears any in-memory admin context cleanly.
      window.location.assign("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start preview.");
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid="merchant-view-as-button"
      title="Open the merchant's back-office as them (audit-logged, 60-min session)"
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/[0.08] hover:bg-amber-400/[0.16] text-amber-100 px-4 py-2 text-[12.5px] font-medium transition-colors disabled:opacity-50 disabled:cursor-wait"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
      View as merchant
    </button>
  );
}

